import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IGenericCrudConfiguration } from 'src/app/libs/components/generic-crud/generic-crud.component';
import {
  BatchCodeDefinitions,
  BatchSelectionCriteria,
  BatchSplittingMode,
  BatchTraceability,
  BatchType,
  ProviderBatchCodeDefinitions,
} from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { BatchTypesAddComponent } from './batch-types-add/batch-types-add.component';
import { BatchTypesEditComponent } from './batch-types-edit/batch-types-edit.component';

/** Valor de lookup: nom d'enum tal com el retorna l'API (string), o legacy numèric. */
function coerceApiEnumCode(
  row: Record<string, unknown>,
  camel: string,
  pascal: string,
  definitions: readonly { id: number; name: string }[],
): string | undefined {
  const v = row[camel] ?? row[pascal];
  if (v === null || v === undefined || v === '') {
    return undefined;
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') {
      return undefined;
    }
    if (definitions.some((d) => d.name === trimmed)) {
      return trimmed;
    }
    return undefined;
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return definitions.find((d) => d.id === v)?.name;
  }
  return undefined;
}

function rowIdField(row: Record<string, unknown>, camel: string, pascal: string): unknown {
  return row[camel] ?? row[pascal];
}

@Component({
  selector: 'app-batch-types',
  templateUrl: './batch-types.component.html',
  styleUrl: './batch-types.component.sass',
  standalone: false,
})
export class BatchTypesComponent implements OnInit {
  configuration?: IGenericCrudConfiguration<BatchType>;

  constructor(
    private api: MasterApiService,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    const companyId = this.api.auth.userValue?.lastCompanyId ?? '';
    const productBatchStates = await firstValueFrom(
      this.api.resources.product.productBatchStates.getAll({ companyId }),
    );
    const productBatchStateMap = new Map(
      productBatchStates.map((state) => [state.id, state])
    );

    const i18nKeys = [
      ...BatchCodeDefinitions.map((x) => `enumeration.BatchCodeDefinition.${x.name}`),
      ...ProviderBatchCodeDefinitions.map((x) => `enumeration.ProviderBatchCodeDefinition.${x.name}`),
      ...BatchSelectionCriteria.map((x) => `enumeration.BatchSelectionCriteria.${x.name}`),
      ...BatchTraceability.map((x) => `enumeration.BatchTraceability.${x.name}`),
      ...BatchSplittingMode.map((x) => `enumeration.BatchSplittingMode.${x.name}`),
    ];
    await firstValueFrom(this.translate.get(i18nKeys));

    const batchCodeDefinitions = BatchCodeDefinitions.map((x) => ({
      code: x.name,
      name: this.translate.instant(`enumeration.BatchCodeDefinition.${x.name}`),
    }));
    const providerBatchCodeDefinitions = ProviderBatchCodeDefinitions.map((x) => ({
      code: x.name,
      name: this.translate.instant(`enumeration.ProviderBatchCodeDefinition.${x.name}`),
    }));
    const batchSelectionCriteria = BatchSelectionCriteria.map((x) => ({
      code: x.name,
      name: this.translate.instant(`enumeration.BatchSelectionCriteria.${x.name}`),
    }));
    const batchTraceability = BatchTraceability.map((x) => ({
      code: x.name,
      name: this.translate.instant(`enumeration.BatchTraceability.${x.name}`),
    }));
    const batchSplittingMode = BatchSplittingMode.map((x) => ({
      code: x.name,
      name: this.translate.instant(`enumeration.BatchSplittingMode.${x.name}`),
    }));

    this.configuration = {
      resource: this.api.resources.product.batchTypes,
      entityFields: {
        name: { isLink: true, dataType: 'string' },
        batchTraceability: {
          dataType: 'code',
          calculateCellValue: (row) =>
            coerceApiEnumCode(row as unknown as Record<string, unknown>, 'batchTraceability', 'BatchTraceability', BatchTraceability),
          lookup: { dataSource: batchTraceability, displayExpr: 'name', valueExpr: 'code' },
        },
        batchCodeDefinition: {
          dataType: 'string',
          calculateCellValue: (row) =>
            coerceApiEnumCode(row as unknown as Record<string, unknown>, 'batchCodeDefinition', 'BatchCodeDefinition', BatchCodeDefinitions),
          lookup: { dataSource: batchCodeDefinitions, displayExpr: 'name', valueExpr: 'code' },
        },
        providerBatchCodeDefinition: {
          dataType: 'string',
          calculateCellValue: (row) =>
            coerceApiEnumCode(
              row as unknown as Record<string, unknown>,
              'providerBatchCodeDefinition',
              'ProviderBatchCodeDefinition',
              ProviderBatchCodeDefinitions,
            ),
          lookup: { dataSource: providerBatchCodeDefinitions, displayExpr: 'name', valueExpr: 'code' },
        },
        batchSelectionCriteria: {
          dataType: 'code',
          calculateCellValue: (row) =>
            coerceApiEnumCode(
              row as unknown as Record<string, unknown>,
              'batchSelectionCriteria',
              'BatchSelectionCriteria',
              BatchSelectionCriteria,
            ),
          lookup: { dataSource: batchSelectionCriteria, displayExpr: 'name', valueExpr: 'code' },
        },
        batchSplittingMode: {
          dataType: 'shortstring',
          calculateCellValue: (row) =>
            coerceApiEnumCode(row as unknown as Record<string, unknown>, 'batchSplittingMode', 'BatchSplittingMode', BatchSplittingMode),
          lookup: { dataSource: batchSplittingMode, displayExpr: 'name', valueExpr: 'code' },
        },
        defaultBatchStateId: {
          dataType: 'code',
          cellTemplate: 'productBatchStateColorTpl',
          calculateCellValue: (row) => {
            const stateId = rowIdField(
              row as unknown as Record<string, unknown>,
              'defaultBatchStateId',
              'DefaultBatchStateId'
            );

            const state = productBatchStateMap.get(stateId as any);

            if (state) {
              (row as any).productBatchStateName = state.name;
              (row as any).productBatchStateColor = state.color;
            }

            return stateId;
          },
          lookup: {
            dataSource: productBatchStates,
            displayExpr: 'name',
            valueExpr: 'id',
          },
        },
        batchCodeRangeMin: {
          dataType: 'shortstring',
        },
        batchCodeRangeMax: {
          dataType: 'shortstring',
        }
      },
      editComponent: BatchTypesEditComponent,
      createComponent: BatchTypesAddComponent,
    };
  }
}
